import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ScrollText, Search, Filter, RefreshCw,
  User, Package, Users, ShoppingCart, ClipboardList, 
  Truck, Settings, ChevronLeft, ChevronRight,
  Plus, Edit, Trash2, RotateCcw, Power, Key, CheckCircle,
  Mail, ArrowUpDown, AlertTriangle, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const actionLabels = {
  create: 'Tambah',
  update: 'Edit',
  delete: 'Hapus',
  stock_update: 'Stok',
  status_change: 'Status',
  receive: 'Terima',
  resend_verification: 'Resend',
  purge: 'Purge'
};

const actionColors = {
  create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  update: 'bg-blue-50 text-blue-700 border-blue-200',
  delete: 'bg-rose-50 text-rose-700 border-rose-200',
  stock_update: 'bg-amber-50 text-amber-700 border-amber-200',
  status_change: 'bg-teal-50 text-teal-700 border-teal-200',
  receive: 'bg-teal-50 text-teal-700 border-teal-200',
  resend_verification: 'bg-teal-50 text-teal-700 border-teal-200',
  purge: 'bg-rose-50 text-rose-700 border-rose-200'
};

const actionIcons = {
  create: <Plus size={12} />,
  update: <Edit size={12} />,
  delete: <Trash2 size={12} />,
  stock_update: <ArrowUpDown size={12} />,
  status_change: <RotateCcw size={12} />,
  receive: <CheckCircle size={12} />,
  resend_verification: <Mail size={12} />,
  purge: <Trash2 size={12} />
};

const entityLabels = {
  customer: 'Pelanggan',
  product: 'Produk',
  transaction: 'Transaksi',
  purchase_order: 'PO',
  user: 'User',
  settings: 'Setting',
  audit_log: 'Audit Log'
};

const entityIcons = {
  customer: <Users size={14} className="text-blue-500" />,
  product: <Package size={14} className="text-emerald-500" />,
  transaction: <ShoppingCart size={14} className="text-teal-500" />,
  purchase_order: <ClipboardList size={14} className="text-amber-500" />,
  user: <User size={14} className="text-teal-600" />,
  settings: <Settings size={14} className="text-slate-500" />,
  audit_log: <ScrollText size={14} className="text-rose-500" />
};

const AuditLogModule = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const pageSize = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityFilter !== 'all') params.append('entity_type', entityFilter);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      params.append('limit', pageSize);
      params.append('skip', page * pageSize);
      
      const { data } = await axios.get(`${API}/audit-logs?${params.toString()}`, { withCredentials: true });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [entityFilter, actionFilter, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const openDeleteModal = () => {
    setConfirmText('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
  };

  const handleDeleteAll = async () => {
    if (confirmText.trim().toUpperCase() !== 'HAPUS') {
      setDeleteError('Ketik HAPUS untuk konfirmasi.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      const { data } = await axios.delete(`${API}/audit-logs`, { withCredentials: true });
      setShowDeleteModal(false);
      setDeleteSuccess(`${data.deleted || 0} riwayat audit log berhasil dihapus.`);
      setTimeout(() => setDeleteSuccess(''), 4000);
      setPage(0);
      await fetchLogs();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Gagal menghapus audit log.';
      setDeleteError(typeof msg === 'string' ? msg : 'Gagal menghapus audit log.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      log.entity_name?.toLowerCase().includes(q) ||
      log.user_name?.toLowerCase().includes(q) ||
      log.user_email?.toLowerCase().includes(q) ||
      log.details?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(total / pageSize);

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const d = new Date(timestamp);
    return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) + 
      ' ' + d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6 animate-in" data-testid="audit-log-module">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">Audit Log</h2>
          <p className="text-muted-foreground mt-1">Riwayat aktivitas pengguna dalam sistem</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => { setPage(0); fetchLogs(); }} 
            className="btn-secondary"
            data-testid="refresh-audit-log"
          >
            <RefreshCw size={16}/> Refresh
          </button>
          <button
            onClick={openDeleteModal}
            className="btn-destructive"
            data-testid="delete-audit-log-btn"
            disabled={total === 0}
            title={total === 0 ? 'Tidak ada riwayat untuk dihapus' : 'Hapus seluruh riwayat audit log'}
          >
            <Trash2 size={16}/> Hapus Audit Log
          </button>
        </div>
      </div>

      {deleteSuccess && (
        <div
          className="card p-3 px-4 flex items-center gap-3 border-emerald-200 bg-emerald-50 text-emerald-800"
          data-testid="delete-audit-log-success"
        >
          <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" />
          <span className="text-sm font-medium">{deleteSuccess}</span>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row gap-4 justify-between">
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Cari aktivitas..."
              className="input-field pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-audit-log"
            />
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-muted-foreground" />
              <select 
                className="input-field w-auto text-sm"
                value={entityFilter}
                onChange={(e) => { setEntityFilter(e.target.value); setPage(0); }}
                data-testid="entity-filter"
              >
                <option value="all">Semua Entitas</option>
                <option value="customer">Pelanggan</option>
                <option value="product">Produk</option>
                <option value="transaction">Transaksi</option>
                <option value="purchase_order">Purchase Order</option>
                <option value="user">User</option>
              </select>
            </div>
            <select 
              className="input-field w-auto text-sm"
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
              data-testid="action-filter"
            >
              <option value="all">Semua Aksi</option>
              <option value="create">Tambah</option>
              <option value="update">Edit</option>
              <option value="delete">Hapus</option>
              <option value="stock_update">Update Stok</option>
              <option value="status_change">Ubah Status</option>
              <option value="receive">Terima Barang</option>
            </select>
          </div>
        </div>
      </div>

      {/* Log Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="table-header">
              <tr>
                <th className="px-3 py-4 text-left w-[12%]">Waktu</th>
                <th className="px-3 py-4 text-left w-[18%]">Pengguna</th>
                <th className="px-3 py-4 text-center w-[9%]">Aksi</th>
                <th className="px-3 py-4 text-center w-[9%]">Entitas</th>
                <th className="px-3 py-4 text-left w-[20%]">Nama</th>
                <th className="px-3 py-4 text-left w-[32%]">Detail</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="px-3 py-12 text-center text-muted-foreground">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    Memuat data...
                  </div>
                </td></tr>
              ) : filteredLogs.length === 0 ? (
                <tr><td colSpan="6" className="px-3 py-12 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <ScrollText size={40} className="text-muted-foreground/30" />
                    <p>{searchQuery || entityFilter !== 'all' || actionFilter !== 'all' ? 'Tidak ada log yang sesuai filter.' : 'Belum ada aktivitas tercatat.'}</p>
                  </div>
                </td></tr>
              ) : filteredLogs.map((log, idx) => (
                <tr key={idx} className="table-row" data-testid={`audit-log-row-${idx}`}>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">
                    {formatTime(log.timestamp)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-500 to-cyan-700 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                        {log.user_name?.charAt(0)?.toUpperCase() || 'S'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-xs truncate">{log.user_name || 'System'}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{log.user_email || ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${actionColors[log.action] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {actionIcons[log.action] || null}
                      {actionLabels[log.action] || log.action}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap">
                      {entityIcons[log.entity_type] || null}
                      {entityLabels[log.entity_type] || log.entity_type}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-medium text-sm truncate">
                    {log.entity_name || '-'}
                  </td>
                  <td className="px-3 py-3 text-muted-foreground text-xs truncate">
                    {log.details || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border/60">
            <p className="text-sm text-muted-foreground">
              Menampilkan {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} dari {total} log
            </p>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="audit-prev-page"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm font-medium px-2">{page + 1} / {totalPages}</span>
              <button 
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="audit-next-page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Audit Log Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-audit-log-modal">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/55 backdrop-blur-sm"
              onClick={closeDeleteModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle size={18} className="text-rose-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg leading-tight">Hapus Seluruh Audit Log?</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Aksi ini hanya bisa dilakukan Super Admin</p>
                  </div>
                </div>
                <button
                  onClick={closeDeleteModal}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-muted-foreground"
                  data-testid="delete-audit-log-close-btn"
                  disabled={deleting}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <p className="text-foreground">
                  Anda akan menghapus <strong>{total}</strong> entri riwayat audit log secara permanen.
                  Tindakan ini <strong className="text-rose-600">tidak dapat dibatalkan</strong>.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-amber-800 text-xs">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>Setelah dihapus, riwayat tidak dapat dipulihkan. Pertimbangkan export terlebih dahulu jika diperlukan untuk audit/compliance.</span>
                </div>
                <div>
                  <label className="label">Ketik <span className="font-mono text-rose-600">HAPUS</span> untuk konfirmasi</label>
                  <input
                    type="text"
                    autoFocus
                    value={confirmText}
                    onChange={(e) => { setConfirmText(e.target.value); setDeleteError(''); }}
                    placeholder="HAPUS"
                    className="input-field"
                    disabled={deleting}
                    data-testid="delete-audit-log-confirm-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDeleteAll(); }}
                  />
                </div>
                {deleteError && (
                  <p className="text-xs text-rose-600 font-medium" data-testid="delete-audit-log-error">{deleteError}</p>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeDeleteModal}
                  className="btn-secondary flex-1"
                  disabled={deleting}
                  data-testid="delete-audit-log-cancel"
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="btn-destructive flex-1"
                  disabled={deleting || confirmText.trim().toUpperCase() !== 'HAPUS'}
                  data-testid="delete-audit-log-confirm"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Menghapus...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16}/> Ya, Hapus Semua
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AuditLogModule;
